// Источники кадров для превью на таймлайне.
//
// Единый интерфейс:
//   {
//     kind: "sprite-vtt" | "screenshots" | "seek",
//     ready: Promise<void>,          // когда можно рисовать
//     aspect: number | null,         // ширина/высота кадра, когда известно
//     getFrame(timeSec) -> Frame | null,
//     subscribe(cb) -> unsubscribe,  // сообщает "кадр обновился" (нужно только для seek)
//     destroy(),
//   }
//   Frame = { type: "sprite", url, x, y, w, h }
//         | { type: "image",  url }
//         | { type: "canvas", canvas }
//
// Выбор источника — createFrameSource() внизу файла.

// ---------------------------------------------------------------------------
// 1. Спрайт + WebVTT-раскадровка (EdgeCenter/Gcore: поля sprite / sprite_vtt,
//    или свой спрайт из tools/make-sprite.sh). Самый точный и дешёвый вариант.
// ---------------------------------------------------------------------------
export function createSpriteVttFrameSource({ vttUrl, fetchImpl }) {
  const doFetch = fetchImpl || ((u) => fetch(u, { credentials: "omit" }));
  let cues = [];
  let aspect = null;
  const images = new Map();

  const ready = doFetch(vttUrl)
    .then((r) => {
      if (!r.ok) throw new Error(`sprite vtt: HTTP ${r.status}`);
      return r.text();
    })
    .then((text) => {
      cues = parseSpriteVtt(text, vttUrl);
      if (cues.length) aspect = cues[0].w / cues[0].h;
      // Прогреваем картинки спрайта (обычно один-два файла).
      for (const url of new Set(cues.map((c) => c.url))) {
        if (!images.has(url)) {
          const img = new Image();
          img.decoding = "async";
          img.src = url;
          images.set(url, img);
        }
      }
    });

  function getFrame(t) {
    if (!cues.length || !isFinite(t)) return null;
    // Бинарный поиск по start.
    let lo = 0, hi = cues.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (cues[mid].start <= t) lo = mid; else hi = mid - 1;
    }
    const c = cues[lo];
    return { type: "sprite", url: c.url, x: c.x, y: c.y, w: c.w, h: c.h };
  }

  return {
    kind: "sprite-vtt",
    ready,
    get aspect() { return aspect; },
    getFrame,
    subscribe: () => () => {},
    destroy: () => { cues = []; images.clear(); },
  };
}

/** Разбор WebVTT с фрагментами `url#xywh=x,y,w,h`. Относительные URL — от адреса VTT. */
export function parseSpriteVtt(text, baseUrl) {
  const cues = [];
  const blocks = text.replace(/\r/g, "").split(/\n\n+/);
  for (const block of blocks) {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    const i = lines.findIndex((l) => l.includes("-->"));
    if (i < 0 || !lines[i + 1]) continue;
    const [a, b] = lines[i].split("-->").map((s) => parseVttTime(s.trim().split(/\s+/)[0]));
    const m = lines[i + 1].match(/^(.*?)#xywh=(\d+),(\d+),(\d+),(\d+)\s*$/);
    if (!m || !isFinite(a)) continue;
    let url = m[1];
    try { url = new URL(m[1], baseUrl).href; } catch (_) { /* оставляем как есть */ }
    cues.push({ start: a, end: b, url, x: +m[2], y: +m[3], w: +m[4], h: +m[5] });
  }
  cues.sort((p, q) => p.start - q.start);
  return cues;
}

function parseVttTime(s) {
  // "HH:MM:SS.mmm" | "MM:SS.mmm"
  const parts = s.split(":").map(parseFloat);
  if (parts.some((p) => !isFinite(p))) return NaN;
  return parts.reduce((acc, p) => acc * 60 + p, 0);
}

// ---------------------------------------------------------------------------
// 2. Список скриншотов, который EdgeCenter уже отдаёт (обычно 5 штук, равномерно
//    по длительности). Грубо, зато работает без изменений на бэкенде — запасной вариант.
// ---------------------------------------------------------------------------
export function createScreenshotsFrameSource({ urls, duration }) {
  const list = (urls || []).filter(Boolean);
  let aspect = null;
  const ready = list.length
    ? new Promise((resolve) => {
        const img = new Image();
        img.onload = () => { aspect = img.naturalWidth / img.naturalHeight; resolve(); };
        img.onerror = () => resolve();
        img.src = list[0];
        list.slice(1).forEach((u) => { const i = new Image(); i.src = u; });
      })
    : Promise.resolve();

  return {
    kind: "screenshots",
    ready,
    get aspect() { return aspect; },
    getFrame(t) {
      if (!list.length || !(duration > 0)) return null;
      const idx = Math.min(list.length - 1, Math.floor((t / duration) * list.length));
      return { type: "image", url: list[Math.max(0, idx)] };
    },
    subscribe: () => () => {},
    destroy() {},
  };
}

// ---------------------------------------------------------------------------
// 3. Скрытый второй плеер: перематываем невидимый <video> на нужное время и
//    рисуем кадр в canvas. Точно до кадра, ничего не нужно на бэкенде, но тянет
//    сегменты по сети — годится как временное решение и для коротких роликов.
//    `Hls` — конструктор из hls.js (передаём снаружи, чтобы не тащить зависимость сюда).
// ---------------------------------------------------------------------------
export function createSeekFrameSource({ hlsUrl, Hls, width = 160, minStepSec = 0.25 }) {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.style.cssText = "position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;left:-9999px";
  document.body.appendChild(video);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const listeners = new Set();
  let hls = null;
  let aspect = null;
  let busy = false;
  let pending = null;
  let lastSeeked = -Infinity;
  let resolveReady;
  const ready = new Promise((r) => { resolveReady = r; });

  if (Hls && Hls.isSupported()) {
    hls = new Hls({
      maxBufferLength: 4,
      maxMaxBufferLength: 8,
      backBufferLength: 0,
      capLevelToPlayerSize: false,
      startLevel: 0,
    });
    hls.on(Hls.Events.MANIFEST_PARSED, (_e, data) => {
      // Самое лёгкое качество: для превью 160px больше не нужно.
      let best = 0;
      data.levels.forEach((l, i) => { if ((l.bitrate || 0) < (data.levels[best].bitrate || 0)) best = i; });
      hls.currentLevel = best;
    });
    hls.loadSource(hlsUrl);
    hls.attachMedia(video);
  } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = hlsUrl; // Safari: нативный HLS
  }

  video.addEventListener("loadedmetadata", () => {
    aspect = video.videoWidth / video.videoHeight || null;
    resolveReady();
  });

  const draw = () => {
    if (!video.videoWidth) return;
    canvas.width = width;
    canvas.height = Math.round(width / (video.videoWidth / video.videoHeight));
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    listeners.forEach((cb) => cb());
  };

  video.addEventListener("seeked", () => {
    draw();
    busy = false;
    if (pending != null) { const t = pending; pending = null; seek(t); }
  });

  function seek(t) {
    if (!isFinite(t)) return;
    if (busy) { pending = t; return; }
    if (Math.abs(t - lastSeeked) < minStepSec) return;
    busy = true;
    lastSeeked = t;
    try { video.currentTime = t; } catch (_) { busy = false; }
  }

  return {
    kind: "seek",
    ready,
    get aspect() { return aspect; },
    getFrame(t) { seek(t); return { type: "canvas", canvas }; },
    subscribe(cb) { listeners.add(cb); return () => listeners.delete(cb); },
    destroy() {
      listeners.clear();
      if (hls) { hls.destroy(); hls = null; }
      video.removeAttribute("src");
      video.load();
      video.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Автовыбор по данным версии (ContentVersionDetailedView):
//   spriteVtt -> sprite-vtt;  hlsUrl + Hls -> seek;  screenshots -> screenshots.
// `prefer` позволяет переставить порядок, напр. ["sprite-vtt", "screenshots"].
// ---------------------------------------------------------------------------
export function createFrameSource(version, { Hls, duration, prefer = ["sprite-vtt", "seek", "screenshots"], width } = {}) {
  for (const kind of prefer) {
    if (kind === "sprite-vtt" && version?.spriteVtt) {
      return createSpriteVttFrameSource({ vttUrl: version.spriteVtt });
    }
    if (kind === "seek" && version?.hlsUrl && Hls) {
      return createSeekFrameSource({ hlsUrl: version.hlsUrl, Hls, width });
    }
    if (kind === "screenshots" && version?.screenshots?.length) {
      return createScreenshotsFrameSource({ urls: version.screenshots, duration });
    }
  }
  return null;
}
